import { useState, useRef, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  BUTTON_ADD_SELECTED_CONTENT,
  LOADING_CONTENT,
  MODAL_GALLERY_ARIA_LABEL,
  MODAL_GALLERY_YOUTUBE_EMPTY,
  MODAL_GALLERY_YOUTUBE_TITLE,
  YOUTUBE_ACCOUNT_PLACEHOLDER,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import useNearScreen from "../../hooks/useNearScreen";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useDebounced } from "../../hooks/useDebounce";
import ModalBasicLayout from "./ModalBasicLayout";
import Loader from "../Loaders/Loader";
import Button from "../Buttons/Button";
import YoutubeGalleryGrid from "./ModalGalleryYoutube/YoutubeGalleryGrid";
import {
  GOOGLE_PMAX_VIDEO_MAX_DURATION_SECONDS,
  GOOGLE_PMAX_VIDEO_MIN_DURATION_SECONDS,
  YOUTUBE,
} from "../../utils/constants";
import { BUTTON_SHADOW } from "../../utils/colors";
import { useRebrandBanner } from "../../hooks/useRebrandBanner";
import { startGetGalleryAdsPlatforms } from "../../actions/adsCampaigns";
import SelectYouTubeAccountWithConnect from "../Gallery/ShareModalMultiple/components/SelectYouTubeAccountWithConnect";
import { getYoutubeVideoIdFromUrl } from "../../utils/string";

const PAGE_SIZE = 12;

const getWatchUrl = (item) =>
  item?.youtubeUrl?.trim() || item?.url?.trim() || "";

const formatYoutubeGalleryItem = (media) => {
  const watchUrl = media.url;

  return {
    ...media,
    id: media.id,
    url: watchUrl,
    youtubeUrl: watchUrl,
    youtubeVideoId: getYoutubeVideoIdFromUrl({ url: watchUrl }),
    durationSeconds: media.durationSeconds ?? media.duration,
    text: media.text,
    from: YOUTUBE,
  };
};

const formatYoutubeGalleryItems = (mediaList = []) =>
  mediaList.map(formatYoutubeGalleryItem);

const ModalGalleryYoutube = ({
  modalOpen = false,
  onCloseModal = () => {},
  onCallbackSelectedItems = () => {},
  maxSelectable = 1,
  currentUrls = [],
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadLastItem, setLoadLastItem] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [selectedUrls, setSelectedUrls] = useState([]);

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { totalTopOffsetPx } = useRebrandBanner();

  const methods = useForm({
    defaultValues: { youtubeAccount: null },
  });
  const youtubeAccount = methods.watch("youtubeAccount");
  const selectedIntegrationID =
    youtubeAccount?.id || youtubeAccount?.integrationID;

  const galleryRef = useRef(null);
  const lastKeyRef = useRef(null);
  const prevIntegrationNotFoundModalOpen = useRef(false);

  const integrationNotFoundModalOpen = useSelectorApp(
    (state) => state?.alerts?.integrationNotFound?.open
  );

  const { isNearScreen, fromRef } = useNearScreen({
    distance: "100px",
    once: false,
    disabledTimeout: true,
  });

  const remainingSlots = Math.max(
    0,
    maxSelectable - (currentUrls || []).filter(Boolean).length
  );

  const importedUrls = useMemo(
    () =>
      new Set(
        (currentUrls || [])
          .filter(Boolean)
          .map((url) => url.trim().toLowerCase())
      ),
    [currentUrls]
  );

  const fetchYoutubeGalleryPage = async ({ pageLastKey } = {}) => {
    const response = await dispatch(
      startGetGalleryAdsPlatforms({
        platform: YOUTUBE,
        integrationID: selectedIntegrationID,
        adAccountID: undefined,
        pageID: undefined,
        lastKey: pageLastKey,
        onChangeLastKey: (key) => {
          lastKeyRef.current = key;
        },
        onChangeLoadLastItem: setLoadLastItem,
        onChangeIsCalling: setIsCalling,
        limit: PAGE_SIZE,
        from: YOUTUBE,
        minDurationSeconds: GOOGLE_PMAX_VIDEO_MIN_DURATION_SECONDS,
        maxDurationSeconds: GOOGLE_PMAX_VIDEO_MAX_DURATION_SECONDS,
      })
    );

    return {
      ok: Boolean(response?.ok),
      items: response?.ok
        ? formatYoutubeGalleryItems(response?.data || [])
        : [],
    };
  };

  const getInitialData = async () => {
    if (!selectedIntegrationID) return;

    setLoading(true);
    setData([]);
    setSelectedUrls([]);
    setLoadLastItem(false);
    lastKeyRef.current = null;

    const page = await fetchYoutubeGalleryPage({ pageLastKey: undefined });
    if (page.ok) {
      setData(page.items);
    }

    setLoading(false);
  };

  const getNextMedia = useDebounced(async () => {
    if (loadLastItem || isCalling || !selectedIntegrationID) return;

    const page = await fetchYoutubeGalleryPage({
      pageLastKey: lastKeyRef.current,
    });
    if (page.ok && page.items.length > 0) {
      setData((prevState) => [...prevState, ...page.items]);
    }
  }, 1000);

  useEffect(() => {
    if (modalOpen && selectedIntegrationID) {
      getInitialData();
    }
    // eslint-disable-next-line
  }, [modalOpen, selectedIntegrationID]);

  useEffect(() => {
    if (isNearScreen && !loadLastItem) getNextMedia();
    // eslint-disable-next-line
  }, [isNearScreen]);

  useEffect(() => {
    const wasOpen = prevIntegrationNotFoundModalOpen.current;
    const isNowClosed = wasOpen && !integrationNotFoundModalOpen;

    if (isNowClosed && modalOpen) {
      getInitialData();
    }

    prevIntegrationNotFoundModalOpen.current = integrationNotFoundModalOpen;
    // eslint-disable-next-line
  }, [integrationNotFoundModalOpen, modalOpen]);

  useEffect(() => {
    if (!modalOpen) {
      setData([]);
      setSelectedUrls([]);
      lastKeyRef.current = null;
      methods.reset({ youtubeAccount: null });
    }
    // eslint-disable-next-line
  }, [modalOpen]);

  useEffect(() => {
    if (modalOpen && remainingSlots <= 0) {
      onCloseModal();
    }
  }, [modalOpen, remainingSlots, onCloseModal]);

  const onSelectItem = (item) => {
    const watchUrl = getWatchUrl(item);
    if (!watchUrl) return;

    const normalizedUrl = watchUrl.toLowerCase();
    if (importedUrls.has(normalizedUrl)) {
      return;
    }

    setSelectedUrls((prevSelected) => {
      const isAlreadySelected = prevSelected.some(
        (url) => url.toLowerCase() === normalizedUrl
      );

      if (isAlreadySelected) {
        return prevSelected.filter((url) => url.toLowerCase() !== normalizedUrl);
      }

      if (prevSelected.length >= remainingSlots) {
        return prevSelected;
      }

      return [...prevSelected, watchUrl];
    });
  };

  const onSendSelectedItems = () => {
    if (selectedUrls.length === 0) return;

    const urlsToImport = selectedUrls.slice(0, remainingSlots);
    onCallbackSelectedItems(
      urlsToImport.map((youtubeUrl) => ({ youtubeUrl }))
    );
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth
      maxWidth="xl"
      scroll="paper"
      aria-label={t(MODAL_GALLERY_ARIA_LABEL)}
      sxPaper={{
        height: "calc(100% - 8%)",
      }}
      sxContent={{
        p: 0,
        pl: 3,
        pb: 12,
        mt: 1,
        overflowX: "hidden",
        overflowY: "auto",
        "::-webkit-scrollbar": {
          width: 0,
          height: 0,
          background: "transparent",
        },
      }}
      refContent={galleryRef}
      title={t(MODAL_GALLERY_YOUTUBE_TITLE)}
    >
      <FormProvider {...methods}>
        <Box sx={{ mb: 3, pr: 3 }}>
          <SelectYouTubeAccountWithConnect
            control={methods.control}
            label={t(YOUTUBE_ACCOUNT_PLACEHOLDER)}
            name="youtubeAccount"
            fullWidth
            variant="filled"
            errors={methods.formState.errors}
          />
        </Box>

        {loading && (
          <Loader size={70} hasMessage={true} message={t(LOADING_CONTENT)} />
        )}

        {!loading && selectedIntegrationID && data.length > 0 && (
          <YoutubeGalleryGrid
            items={data}
            importedUrls={importedUrls}
            selectedUrls={selectedUrls}
            onSelectItem={onSelectItem}
            lastItemRef={fromRef}
          />
        )}

        {!loading && selectedIntegrationID && data.length === 0 && loadLastItem && (
          <Box
            sx={{
              height: `calc(100% - ${totalTopOffsetPx}px)`,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ml: "-12px",
              mb: 4,
            }}
          >
            <Typography variant="h4" align="center">
              {t(MODAL_GALLERY_YOUTUBE_EMPTY)}
            </Typography>
          </Box>
        )}

        {selectedUrls.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              py: 1,
              mt: 2,
              pb: 2,
              pr: 3,
            }}
          >
            <Button
              color="primary"
              sx={{
                boxShadow: BUTTON_SHADOW,
                "&:hover": {
                  boxShadow: BUTTON_SHADOW,
                },
              }}
              onClick={(event) => {
                event?.preventDefault();
                event?.stopPropagation();
                onSendSelectedItems();
              }}
            >
              {t(BUTTON_ADD_SELECTED_CONTENT)}
            </Button>
          </Box>
        )}
      </FormProvider>
    </ModalBasicLayout>
  );
};

export default ModalGalleryYoutube;
